import {
  FormControlLabel,
  FormGroup,
  Grid,
  Icon,
  Switch,
  Checkbox,
  InputLabel,
  TextareaAutosize,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  ListSubheader,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDSelect from "components/MDSelect/MDSelect";
import MDTypography from "components/MDTypography";
import {
  CreateNotificationTriggerInput,
  NotificationTriggerAdditionalRecipientTypes,
  NotificationTriggerFragmentFragment,
  NotificationTriggerType,
  UpdateNotificationTriggerInput,
} from "generated/graphql";
import { useState, useMemo, useCallback, useRef } from "react";
import { Controller, FieldErrors, useFieldArray, useForm } from "react-hook-form";
import FormField from "components/FormField/FormField";
import * as z from "zod";

import useGetOrganizationUsers from "hooks/users/useGetOrganizationUsers";
import CustomMultipleSelect from "components/Shared/CustomMultipleSelect/CustomMultipleSelect";
import WYSIWYG from "components/WYSIWYG/WYSIWYG";
import MDButton from "components/MDButton";
import { capitalCase } from "change-case";
import { zodResolver } from "@hookform/resolvers/zod";
import { notificationTriggerEventsToTypes } from "../../constants/notificationTriggerEventsToTypes";
import { contentPlaceHolderTypes, PlaceHolderTypes } from "../../constants/PlaceholderTypes";
import PlaceHolderSelect from "components/PlaceHolderSelect/PlaceHolderSelect";
import TestApiDialog from "modules/email-triggers/TestApiDialog/TestApiDialog";

type NotificationTriggerFormInput = CreateNotificationTriggerInput | UpdateNotificationTriggerInput;
type NotificationTriggerFormValues = NotificationTriggerFormInput & {
  isTask?: boolean;
  taskContent?: string;
  customHeaders?: { name: string; value: string }[];
  requestFields?: { key: string; value: string }[];
};
interface NotificationTriggerFormProps {
  onSubmit: (values: NotificationTriggerFormInput) => void;
  loading?: boolean;
  notificationTrigger?: NotificationTriggerFragmentFragment;
}

const optionalObject = z
  .object({
    sendToContact: z.boolean(),
    sendToUser: z.boolean(),
    sendToAllRecipients: z.boolean(),
    additionalRecipientTypes: z.array(z.string().or(z.null())),
  })
  .partial();

function getFormErrorMessages(errors: FieldErrors<NotificationTriggerFormValues>) {
  const messages = new Set<string>();

  const collectMessages = (value: unknown) => {
    if (!value || typeof value !== "object") return;

    if ("message" in value && typeof value.message === "string") {
      messages.add(value.message);
    }

    Object.values(value).forEach(collectMessages);
  };

  collectMessages(errors);
  return Array.from(messages);
}

const schema = z
  .object({
    type: z.string().min(1, "Type is required"),
    event: z.string().min(1, "Event is required"),
    active: z.boolean(),

    smsContent: z.string().max(190, "SMS content cannot exceed 190 characters").optional(),
    isEmail: z.boolean(),
    isSMS: z.boolean(),
    content: z.string(),
    name: z.string().min(1, "Name is required"),
    delay: z.number().min(0, "Delay is required"),
    emailSubject: z.string(),
    isTask: z.boolean().optional(),
    taskContent: z.string().optional(),

    additionalRecipients: z.object({
      sync: z.array(z.string()),
    }),

    isApi: z.boolean().optional(),
    apiUrl: z.string().optional(),
    apiMethod: z.string().optional(),
    customHeaders: z.array(z.object({ name: z.string(), value: z.string() })).optional(),
    requestFields: z.array(z.object({ key: z.string(), value: z.string() })).optional(),
  })
  .merge(optionalObject)
  .superRefine(
    (
      {
        isApi,
        apiUrl,
        apiMethod,
        content,
        emailSubject,
        isEmail,
        isSMS,
        smsContent,
        type,
        sendToContact,
        sendToUser,
        sendToAllRecipients,
        additionalRecipientTypes,
        additionalRecipients,
        isTask,
        taskContent,
      },
      ctx
    ) => {
      const isContactTrigger = type === NotificationTriggerType.CONTACT;
      const hasRecipient =
        !!sendToContact ||
        !!sendToUser ||
        !!sendToAllRecipients ||
        additionalRecipientTypes.length > 0 ||
        additionalRecipients.sync.length > 0;
      const needsRecipients = !isApi && (isEmail || isSMS) && !isContactTrigger;

      if (!isApi && isEmail && !content?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["content"],
          message: "Content is required",
        });
      }

      if (!isApi && isEmail && !emailSubject?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["emailSubject"],
          message: "Email subject is required when Email Trigger is enabled",
        });
      }

      if (!isApi && isSMS && !smsContent?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["smsContent"],
          message: "SMS content is required when SMS Trigger is enabled",
        });
      }

      if (isApi && !apiUrl?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["apiUrl"],
          message: "URL is required for API triggers",
        });
      } else if (isApi && !z.string().url().safeParse(apiUrl).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["apiUrl"],
          message: "Enter a valid endpoint URL",
        });
      }

      if (isApi && apiMethod !== "GET" && apiMethod !== "POST") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["apiMethod"],
          message: "API method must be GET or POST",
        });
      }

      if (needsRecipients && !hasRecipient) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sendToContact"],
          message: "Must choose at least one recipient",
        });
      }

      if (!isApi && isTask && !taskContent?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["taskContent"],
          message: "Task content is required when Create Task is enabled",
        });
      }

      if (
        !isApi &&
        isTask &&
        !sendToUser &&
        additionalRecipientTypes.length < 1 &&
        additionalRecipients.sync.length < 1
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sendToUser"],
          message:
            "Create Task requires at least one internal recipient (User/Role/Additional Recipients)",
        });
      }
    }
  );

function PlaceholderTextInput({
  label,
  placeholder,
  inputId,
  placeholderType,
  value = "",
  onChange,
  ...rest
}: {
  label: string;
  placeholder?: string;
  inputId: string;
  placeholderType?: PlaceHolderTypes;
  value?: string;
  onChange: (value: string) => void;
  [key: string]: any;
}) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const cursorPosRef = useRef<number>(0);
  const placeHolders = placeholderType ? contentPlaceHolderTypes[placeholderType] : null;

  const handleAdornmentClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const input = document.getElementById(inputId) as HTMLInputElement | null;
    cursorPosRef.current = input?.selectionStart ?? value.length;
    setMenuAnchor(e.currentTarget);
  };

  const handleSelect = (token: string) => {
    const pos = cursorPosRef.current;
    const newValue = `${value.slice(0, pos)}${token}${value.slice(pos)}`;
    onChange(newValue);
    cursorPosRef.current = pos + token.length;
    setMenuAnchor(null);
  };

  return (
    <>
      <FormField
        {...rest}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        inputProps={{ id: inputId }}
        InputProps={
          placeHolders
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Insert placeholder">
                      <IconButton
                        size="small"
                        onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                        onClick={handleAdornmentClick}
                      >
                        <Icon fontSize="small">data_object</Icon>
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }
            : undefined
        }
      />
      {placeHolders && (
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          sx={{ maxHeight: 400 }}
        >
          {Object.keys(placeHolders).map((group) => [
            <ListSubheader key={group}>{group.replace(/([A-Z])/g, " $1").trim()}</ListSubheader>,
            Object.keys(placeHolders[group]).map((k) => (
              <MenuItem key={k} onClick={() => handleSelect(`{{${k}}}`)}>
                {placeHolders[group][k]}
              </MenuItem>
            )),
          ])}
        </Menu>
      )}
    </>
  );
}

export default function NotificationTriggerForm({
  onSubmit,
  loading,
  notificationTrigger,
}: NotificationTriggerFormProps) {
  const [testOpen, setTestOpen] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const errorSummaryRef = useRef<HTMLDivElement>(null);

  const taskDefaults = (notificationTrigger ?? {}) as unknown as {
    isTask?: boolean;
    taskContent?: string;
  };

  const parsedFields = (() => {
    try {
      return JSON.parse(notificationTrigger?.apiPayload ?? "") ?? [];
    } catch {
      return [];
    }
  })();

  const parsedHeaders = (() => {
    try {
      return JSON.parse(notificationTrigger?.apiHeaders ?? "") ?? [];
    } catch {
      return [];
    }
  })();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    clearErrors,
    getValues,
    watch,
  } = useForm<NotificationTriggerFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      id: notificationTrigger?.id,
      type: notificationTrigger?.type,
      event: notificationTrigger?.event,
      content: notificationTrigger?.content ?? "",
      isEmail: notificationTrigger?.isEmail ?? false,
      isSMS: notificationTrigger?.isSMS ?? false,
      smsContent: notificationTrigger?.smsContent ?? "",
      sendToContact: notificationTrigger?.sendToContact ?? false,
      isTask: taskDefaults.isTask ?? false,
      taskContent: taskDefaults.taskContent ?? "",
      name: notificationTrigger?.name,
      delay: notificationTrigger?.delay ?? 0,
      additionalRecipients: notificationTrigger
        ? { sync: notificationTrigger?.additionalRecipients?.map((r) => r.id) }
        : { sync: [] },
      additionalRecipientTypes: notificationTrigger?.additionalRecipientTypes ?? [],
      sendToAllRecipients: notificationTrigger?.sendToAllRecipients ?? false,
      active: notificationTrigger?.active ?? false,
      sendToUser: notificationTrigger?.sendToUser ?? false,
      emailSubject: notificationTrigger?.emailSubject ?? "",
      isApi: notificationTrigger?.isApi ?? false,
      apiUrl: notificationTrigger?.apiUrl ?? "",
      apiMethod: notificationTrigger?.apiMethod ?? "POST",
      customHeaders: parsedHeaders,
      requestFields: parsedFields,
    },
  });

  const {
    fields: headerFields,
    append: appendHeader,
    remove: removeHeader,
  } = useFieldArray({ control, name: "customHeaders" });

  const {
    fields: requestFieldEntries,
    append: appendRequestField,
    remove: removeRequestField,
  } = useFieldArray({ control, name: "requestFields" });

  const isApi = watch("isApi") ?? false;
  const apiUrl = watch("apiUrl");
  const formErrorMessages = getFormErrorMessages(errors);

  const handleInvalidSubmit = () => {
    setHasAttemptedSubmit(true);
    requestAnimationFrame(() => errorSummaryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }));
  };

  const { data: organizationUsers, loading: loadingUser } = useGetOrganizationUsers({
    first: 100,
  });

  const [currentType, setCurrentType] = useState<NotificationTriggerType>(
    notificationTrigger?.type
  );

  const setSmsTextAreaValue = (e: any) => {
    const tArea = document.querySelector("#sms-text-area") as any;
    const pos = tArea?.selectionStart ?? 0;
    const textAreaValue = getValues("smsContent") ?? "";
    setValue(
      "smsContent",
      `${textAreaValue.slice(0, pos)}${e.target.value}${textAreaValue.slice(pos)}`
    );
  };

  const setTaskTextAreaValue = (e: any) => {
    const tArea = document.querySelector("#task-text-area") as any;
    const pos = tArea?.selectionStart ?? 0;
    const textAreaValue = getValues("taskContent") ?? "";
    setValue(
      "taskContent",
      `${textAreaValue.slice(0, pos)}${e.target.value}${textAreaValue.slice(pos)}`
    );
  };

  const placeholderTypeKey = useMemo(() => {
    const types: { [k: string]: PlaceHolderTypes } = {
      [NotificationTriggerType.OPPORTUNITY]: "opportunity",
      [NotificationTriggerType.PROPOSAL]: "proposal",
      [NotificationTriggerType.JOB]: "job",
      [NotificationTriggerType.JOB_PHASE]: "jobPhase",
      [NotificationTriggerType.CONTACT]: "contact",
    };

    return types[currentType] ?? null;
  }, [currentType]);

  const getRecipientsLabel = (id: string) => {
    const organizationUser = organizationUsers.find((x) => x.id === id);
    return `${organizationUser?.firstName} ${organizationUser?.lastName}`;
  };

  const handleCopySecret = useCallback(() => {
    const secret = notificationTrigger?.apiSecret;
    if (!secret) return;
    navigator.clipboard.writeText(secret).then(() => {
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    });
  }, [notificationTrigger?.apiSecret]);

  return (
    <MDBox
      role="form"
      component="form"
      onSubmit={handleSubmit(
        ({ customHeaders, requestFields, ...data }) => {
          setHasAttemptedSubmit(false);
          onSubmit({
            ...data,
            apiPayload: JSON.stringify(requestFields ?? []),
            apiHeaders: JSON.stringify(customHeaders ?? []),
          });
        },
        handleInvalidSubmit
      )}
      px={4}
    >
      <MDBox mt={2} py={2}>
        <Grid spacing={3} container py={3}>
          <Grid item xs={12}>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <MDSelect
                  {...field}
                  readOnly={!!notificationTrigger}
                  onChange={(e) => {
                    field.onChange(e.target.value as NotificationTriggerType);
                    setCurrentType(e.target.value as NotificationTriggerType);
                  }}
                  error={!!errors?.type?.message}
                  name="type"
                  value={field.value ?? currentType}
                  fullWidth
                  label="Select Type"
                  options={Object.keys(NotificationTriggerType).map((k) => ({
                    label: capitalCase(k),
                    value: k,
                  }))}
                />
              )}
            />
          </Grid>

          {currentType && (
            <Grid item xs={12}>
              <Controller
                name="event"
                control={control}
                render={({ field }) => (
                  <MDSelect
                    {...field}
                    readOnly={!!notificationTrigger}
                    fullWidth
                    error={!!errors?.event?.message}
                    label="Select Event"
                    options={notificationTriggerEventsToTypes[currentType].map((k) => ({
                      label: capitalCase(k),
                      value: k,
                    }))}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <FormField label="Name" type="text" {...register("name")} />
            {errors?.name?.message && (
              <MDTypography variant="caption" color="error">
                {errors?.name?.message}
              </MDTypography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <>
                  <MDTypography variant="caption">Trigger Status</MDTypography>
                  <Switch {...field} checked={field.value} />
                </>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <FormField
              label="Select Days Delay"
              inputProps={{ step: 1 }}
              type="number"
              {...register("delay", { valueAsNumber: true })}
            />
            {errors?.delay?.message && (
              <MDTypography variant="caption" color="error">
                {errors?.delay?.message}
              </MDTypography>
            )}
          </Grid>

          {!isApi && (
            <>
              <Grid item xs={12}>
                <InputLabel>Recipients</InputLabel>
                {currentType !== NotificationTriggerType.CONTACT && (
                  <>
                    <FormGroup>
                      <Controller
                        name="sendToContact"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            label="Main Contact"
                            control={<Checkbox {...field} checked={field.value} />}
                          />
                        )}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Controller
                        name="sendToUser"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            label={
                              currentType === NotificationTriggerType.OPPORTUNITY
                                ? "Sales Person"
                                : currentType === NotificationTriggerType.JOB
                                ? "Project Manager"
                                : "Estimator"
                            }
                            control={<Checkbox {...field} checked={field.value} />}
                          />
                        )}
                      />
                    </FormGroup>

                    {currentType === NotificationTriggerType.PROPOSAL && (
                      <FormGroup>
                        <Controller
                          name="sendToAllRecipients"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              label="All Proposal Recipients"
                              control={<Checkbox {...field} checked={field.value} />}
                            />
                          )}
                        />
                      </FormGroup>
                    )}

                    {currentType === NotificationTriggerType.JOB_PHASE &&
                      [
                        {
                          label: "Foreman",
                          value: NotificationTriggerAdditionalRecipientTypes.FOREMAN,
                        },
                      ].map((type) => (
                        <FormControlLabel
                          key={type.value}
                          control={
                            <Checkbox
                              defaultChecked={
                                getValues("additionalRecipientTypes").indexOf(type.value) > -1
                              }
                              name={"checkbox"}
                              size="small"
                              value={type.value}
                              {...register("additionalRecipientTypes")}
                            />
                          }
                          label={type.label}
                        />
                      ))}
                  </>
                )}
              </Grid>

              <Grid item xs={12}>
                {organizationUsers?.length > 0 && !loadingUser && (
                  <Controller
                    name="additionalRecipients"
                    control={control}
                    render={({ field }) => (
                      <CustomMultipleSelect
                        {...field}
                        fullWidth
                        onChange={(e) => {
                          field.onChange({
                            sync: e?.map((x) => x.value as string),
                          });
                        }}
                        label="Additional Recipients"
                        value={
                          field.value?.sync?.map((s) => ({
                            label: getRecipientsLabel(s as string),
                            value: s,
                          })) ?? []
                        }
                        options={organizationUsers?.map((user) => ({
                          value: user.id,
                          label: `${user.firstName} ${user.lastName}`,
                        }))}
                      />
                    )}
                  />
                )}
              </Grid>
            </>
          )}

          {errors?.sendToContact?.message && (
            <Grid item xs={12}>
              <MDTypography variant="caption" color="error">
                {errors?.sendToContact?.message}
              </MDTypography>
            </Grid>
          )}

          {errors?.sendToUser?.message && (
            <Grid item xs={12}>
              <MDTypography variant="caption" color="error">
                {errors?.sendToUser?.message}
              </MDTypography>
            </Grid>
          )}

          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">
                <MDTypography variant="caption">Notification Channel</MDTypography>
              </FormLabel>
              <RadioGroup
                row
                value={isApi ? "api" : "email"}
                onChange={(e) => {
                  setValue("isApi", e.target.value === "api");
                  clearErrors();
                }}
              >
                <FormControlLabel value="email" control={<Radio />} label="Email" />
                <FormControlLabel value="api" control={<Radio />} label="API" />
              </RadioGroup>
            </FormControl>
          </Grid>

          {!isApi && (
            <>
              <Grid item xs={12} py="15px">
                <MDTypography variant="h5">Email Settings</MDTypography>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="isEmail"
                  render={({ field }) => (
                    <>
                      <Switch {...field} checked={field.value} />
                      <MDTypography variant="caption">Enable Email Trigger</MDTypography>
                    </>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="emailSubject"
                  render={({ field }) => (
                    <>
                      <WYSIWYG
                        onChange={(e) => field.onChange(e)}
                        title="Email Subject"
                        type={placeholderTypeKey}
                        content={field.value}
                      />
                      {errors?.emailSubject?.message && (
                        <MDTypography variant="caption" color="error">
                          {errors?.emailSubject?.message}
                        </MDTypography>
                      )}
                    </>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="content"
                  render={({ field }) => (
                    <>
                      <WYSIWYG
                        onChange={(e) => field.onChange(e)}
                        title="Content"
                        type={placeholderTypeKey}
                        content={field.value}
                      />
                      {errors?.content?.message && (
                        <MDTypography variant="caption" color="error">
                          {errors?.content?.message}
                        </MDTypography>
                      )}
                    </>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <MDBox sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <MDTypography variant="h5">SMS Settings</MDTypography>
                  {placeholderTypeKey && (
                    <MDBox sx={{ marginLeft: "auto" }}>
                      <PlaceHolderSelect
                        type={placeholderTypeKey}
                        value=""
                        onChange={setSmsTextAreaValue}
                      />
                    </MDBox>
                  )}
                </MDBox>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="isSMS"
                  render={({ field }) => (
                    <>
                      <Switch {...field} checked={field.value} />
                      <MDTypography variant="caption">Enable SMS Trigger</MDTypography>
                    </>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="smsContent"
                  render={({ field }) => (
                    <>
                      <TextareaAutosize
                        {...field}
                        minRows={8}
                        style={{ minWidth: "100%" }}
                        id="sms-text-area"
                      />
                      <MDBox display="flex" justifyContent="space-between" mt={1}>
                        <MDTypography
                          variant="caption"
                          color={field.value?.length > 190 ? "error" : "dark"}
                        >
                          {field.value?.length ?? 0}/190 characters
                        </MDTypography>
                        {errors?.smsContent?.message && (
                          <MDTypography variant="caption" color="error">
                            {errors?.smsContent?.message}
                          </MDTypography>
                        )}
                      </MDBox>
                    </>
                  )}
                />
              </Grid>

              {/* NEW: TASK SETTINGS */}
              <Grid item xs={12} mt={2}>
                <MDBox sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <MDTypography variant="h5">Task Settings</MDTypography>
                  {placeholderTypeKey && (
                    <MDBox sx={{ marginLeft: "auto" }}>
                      <PlaceHolderSelect
                        type={placeholderTypeKey}
                        value=""
                        onChange={setTaskTextAreaValue}
                      />
                    </MDBox>
                  )}
                </MDBox>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="isTask"
                  render={({ field }) => (
                    <>
                      <Switch {...field} checked={!!field.value} />
                      <MDTypography variant="caption">Enable Create Task</MDTypography>
                    </>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="taskContent"
                  render={({ field }) => (
                    <>
                      <TextareaAutosize
                        {...field}
                        minRows={8}
                        style={{ minWidth: "100%" }}
                        id="task-text-area"
                        placeholder="Example: Call customer to follow up on proposal, confirm scope, and answer questions."
                      />
                      {errors?.taskContent?.message && (
                        <MDTypography variant="caption" color="error">
                          {errors?.taskContent?.message}
                        </MDTypography>
                      )}
                    </>
                  )}
                />
              </Grid>
            </>
          )}

          {isApi && (
            <>
              <Grid item xs={12} py="15px">
                <MDTypography variant="h5">API Settings</MDTypography>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="apiUrl"
                  control={control}
                  render={({ field }) => (
                    <FormField
                      label="Endpoint URL"
                      placeholder="https://example.com/webhook"
                      error={errors.apiUrl}
                      {...field}
                    />
                  )}
                />
                {errors?.apiUrl?.message && (
                  <MDTypography variant="caption" color="error">
                    {errors.apiUrl.message}
                  </MDTypography>
                )}
              </Grid>

              <Grid item xs={12}>
                <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
                  HTTP Method
                </MDTypography>
                <Controller
                  name="apiMethod"
                  control={control}
                  render={({ field }) => (
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={field.value ?? "POST"}
                      onChange={(_, val) => {
                        if (val) field.onChange(val);
                      }}
                    >
                      <ToggleButton value="POST">POST</ToggleButton>
                      <ToggleButton value="GET">GET</ToggleButton>
                    </ToggleButtonGroup>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <MDBox mb={1}>
                  <MDTypography variant="caption" fontWeight="medium">
                    Custom Headers
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block">
                    Add headers required by your endpoint (e.g. Authorization, X-API-Key).
                  </MDTypography>
                </MDBox>
                {headerFields.map((headerField, index) => (
                  <MDBox key={headerField.id} display="flex" alignItems="flex-start" gap={1} mb={1}>
                    <MDBox flexBasis="40%" flexShrink={0}>
                      <Controller
                        name={`customHeaders.${index}.name`}
                        control={control}
                        render={({ field }) => (
                          <FormField
                            label="Header name"
                            placeholder="e.g. Authorization"
                            {...field}
                          />
                        )}
                      />
                    </MDBox>
                    <MDBox flexGrow={1}>
                      <Controller
                        name={`customHeaders.${index}.value`}
                        control={control}
                        render={({ field }) => (
                          <PlaceholderTextInput
                            label="Value"
                            placeholder="e.g. Bearer token123"
                            inputId={`header-value-${index}`}
                            placeholderType={placeholderTypeKey || undefined}
                            value={field.value ?? ""}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            ref={field.ref}
                          />
                        )}
                      />
                    </MDBox>
                    <IconButton
                      size="small"
                      onClick={() => removeHeader(index)}
                      sx={{ mt: "28px", flexShrink: 0 }}
                    >
                      <Icon fontSize="small">delete</Icon>
                    </IconButton>
                  </MDBox>
                ))}
                <MDButton
                  size="small"
                  variant="outlined"
                  color="info"
                  onClick={() => appendHeader({ name: "", value: "" })}
                  sx={{ mt: headerFields.length > 0 ? 0.5 : 0 }}
                >
                  <Icon sx={{ mr: 0.5 }}>add</Icon> Add Header
                </MDButton>
              </Grid>

              <Grid item xs={12}>
                <MDBox mb={1}>
                  <MDTypography variant="caption" fontWeight="medium">
                    Request Fields
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block">
                    Add extra key/value pairs to include in the request payload.
                  </MDTypography>
                </MDBox>
                {requestFieldEntries.map((entry, index) => (
                  <MDBox key={entry.id} display="flex" alignItems="flex-start" gap={1} mb={1}>
                    <MDBox flexBasis="40%" flexShrink={0}>
                      <Controller
                        name={`requestFields.${index}.key`}
                        control={control}
                        render={({ field }) => (
                          <FormField label="Key" placeholder="e.g. source" {...field} />
                        )}
                      />
                    </MDBox>
                    <MDBox flexGrow={1}>
                      <Controller
                        name={`requestFields.${index}.value`}
                        control={control}
                        render={({ field }) => (
                          <PlaceholderTextInput
                            label="Value"
                            placeholder="e.g. crm"
                            inputId={`req-field-value-${index}`}
                            placeholderType={placeholderTypeKey || undefined}
                            value={field.value ?? ""}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            ref={field.ref}
                          />
                        )}
                      />
                    </MDBox>
                    <IconButton
                      size="small"
                      onClick={() => removeRequestField(index)}
                      sx={{ mt: "28px", flexShrink: 0 }}
                    >
                      <Icon fontSize="small">delete</Icon>
                    </IconButton>
                  </MDBox>
                ))}
                <MDButton
                  size="small"
                  variant="outlined"
                  color="info"
                  onClick={() => appendRequestField({ key: "", value: "" })}
                  sx={{ mt: requestFieldEntries.length > 0 ? 0.5 : 0 }}
                >
                  <Icon sx={{ mr: 0.5 }}>add</Icon> Add Field
                </MDButton>
              </Grid>

              {notificationTrigger?.apiSecret && (
                <Grid item xs={12}>
                  <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
                    Signing Secret
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block" mb={1}>
                    Use this to verify the <code>X-Bitumio-Signature</code> header on incoming
                    requests.
                  </MDTypography>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <MDBox
                      flexGrow={1}
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "13px",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1.5,
                        wordBreak: "break-all",
                        userSelect: "all",
                      }}
                    >
                      {notificationTrigger.apiSecret}
                    </MDBox>
                    <Tooltip title={secretCopied ? "Copied!" : "Copy secret"}>
                      <IconButton size="small" onClick={handleCopySecret}>
                        <Icon fontSize="small">{secretCopied ? "check" : "content_copy"}</Icon>
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                </Grid>
              )}
            </>
          )}

          <Grid item xs={12}>
            {hasAttemptedSubmit && formErrorMessages.length > 0 && (
              <MDBox
                ref={errorSummaryRef}
                role="alert"
                mb={2}
                p={2}
                sx={{ border: "1px solid", borderColor: "error.main", borderRadius: 1 }}
              >
                <MDTypography variant="caption" color="error" fontWeight="medium" display="block">
                  Please correct the following before saving:
                </MDTypography>
                {formErrorMessages.map((message) => (
                  <MDTypography key={message} variant="caption" color="error" display="block">
                    {message}
                  </MDTypography>
                ))}
              </MDBox>
            )}
            <MDBox display="flex" gap={1} justifyContent="flex-end" mt={2}>
              {isApi && notificationTrigger?.id && (
                <Tooltip
                  title={
                    !notificationTrigger?.id || isDirty ? "Please save the trigger to test" : ""
                  }
                >
                  <span>
                    <MDButton
                      type="button"
                      variant="outlined"
                      color="info"
                      onClick={() => setTestOpen(true)}
                      disabled={!apiUrl || !notificationTrigger?.id || isDirty}
                    >
                      <Icon sx={{ mr: 0.5 }}>play_arrow</Icon> Test Trigger
                    </MDButton>
                  </span>
                </Tooltip>
              )}
              <MDButton variant="gradient" color="success" type="submit" disabled={loading}>
                Save
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      <TestApiDialog
        open={testOpen}
        onClose={() => setTestOpen(false)}
        triggerId={notificationTrigger?.id}
        apiUrl={apiUrl}
      />
    </MDBox>
  );
}
